import React, { useState, useRef } from 'react';
import { Search, ChevronUp, ChevronDown, Edit2, Eye, MoreVertical, CheckSquare, Square, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';

const STATUS_CONFIG = {
  live:    { label: 'Live',    bg: 'rgba(0,255,136,0.12)',  color: '#00FF88', border: 'rgba(0,255,136,0.3)' },
  draft:   { label: 'Draft',   bg: 'rgba(255,184,0,0.12)',  color: '#FFB800', border: 'rgba(255,184,0,0.3)' },
  sold:    { label: 'Sold',    bg: 'rgba(0,212,255,0.12)',  color: '#00D4FF', border: 'rgba(0,212,255,0.3)' },
  expired: { label: 'Expired', bg: 'rgba(255,80,80,0.12)',  color: '#FF5050', border: 'rgba(255,80,80,0.3)' },
};

const PROPERTY_IMAGES = [
  'https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=600&q=80',
  'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=600&q=80',
  'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=600&q=80',
  'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80',
  'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600&q=80',
];

const FILTERS = ['all', 'live', 'draft', 'sold', 'expired'];

const SortIcon = ({ col, sortBy, sortDir }) => {
  if (sortBy !== col) return <ChevronDown size={12} color="#4A7A9B" />;
  return sortDir === 'asc' ? <ChevronUp size={12} color="#00D4FF" /> : <ChevronDown size={12} color="#00D4FF" />;
};

const MobileSwipeableCards = ({ listings, onEdit, onDelete, onStatusChange }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [openMenuId, setOpenMenuId] = useState(null);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const minSwipeDistance = 50;
  const { t } = useLanguage();

  const goTo = (idx) => {
    setCurrentIndex(Math.max(0, Math.min(idx, listings?.length - 1)));
    setOpenMenuId(null);
  };

  const onTouchStart = (e) => {
    touchStartX.current = e?.targetTouches?.[0]?.clientX;
    touchEndX.current = null;
  };

  const onTouchMove = (e) => {
    touchEndX.current = e?.targetTouches?.[0]?.clientX;
  };

  const onTouchEnd = () => {
    if (!touchStartX?.current || !touchEndX?.current) return;
    const distance = touchStartX?.current - touchEndX?.current;
    if (Math.abs(distance) >= minSwipeDistance) {
      if (distance > 0 && currentIndex < listings?.length - 1) goTo(currentIndex + 1);
      else if (distance < 0 && currentIndex > 0) goTo(currentIndex - 1);
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  if (!listings?.length) {
    return <div className="text-center py-10 text-sm" style={{ color: '#4A7A9B' }}>{t('noListingsFound')}</div>;
  }

  const listing = listings?.[currentIndex];
  const sc = STATUS_CONFIG?.[listing?.status] || STATUS_CONFIG?.draft;
  const imgSrc = listing?.image || PROPERTY_IMAGES?.[currentIndex % PROPERTY_IMAGES?.length];

  return (
    <div className="relative">
      {/* Card Counter */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium" style={{ color: '#4A7A9B' }}>
          {currentIndex + 1} {t('ofListings')} {listings?.length} {t('listings')}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => goTo(currentIndex - 1)}
            disabled={currentIndex === 0}
            className="p-1.5 rounded-lg transition-all"
            style={{
              background: currentIndex === 0 ? 'rgba(0,212,255,0.04)' : 'rgba(0,212,255,0.1)',
              color: currentIndex === 0 ? '#2A4A6B' : '#00D4FF',
              border: '1px solid rgba(0,212,255,0.15)'
            }}
          >
            <ChevronLeft size={14} />
          </button>
          <button
            onClick={() => goTo(currentIndex + 1)}
            disabled={currentIndex === listings?.length - 1}
            className="p-1.5 rounded-lg transition-all"
            style={{
              background: currentIndex === listings?.length - 1 ? 'rgba(0,212,255,0.04)' : 'rgba(0,212,255,0.1)',
              color: currentIndex === listings?.length - 1 ? '#2A4A6B' : '#00D4FF',
              border: '1px solid rgba(0,212,255,0.15)'
            }}
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
      {/* Swipeable Card */}
      <div
        className="rounded-2xl overflow-hidden select-none"
        style={{ border: '1px solid rgba(0,212,255,0.18)', background: '#0A1628' }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Property Image Header */}
        <div className="relative h-44 overflow-hidden">
          <img
            src={imgSrc}
            alt={listing?.address || 'Property'}
            className="w-full h-full object-cover"
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
          <div
            className="w-full h-full items-center justify-center"
            style={{ display: 'none', background: 'rgba(0,212,255,0.08)', position: 'absolute', top: 0, left: 0 }}
          >
            <span style={{ fontSize: '48px' }}>🏠</span>
          </div>
          {/* Gradient overlay */}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(10,22,40,0.85) 100%)' }} />
          {/* Status badge on image */}
          <div className="absolute top-3 right-3">
            <span className="px-2.5 py-1 rounded-full text-xs font-bold" style={{
              background: sc?.bg,
              color: sc?.color,
              border: `1px solid ${sc?.border}`,
              backdropFilter: 'blur(8px)'
            }}>
              {sc?.label}
            </span>
          </div>
          {/* Card index dots on image */}
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
            {listings?.slice(0, Math.min(listings?.length, 8))?.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className="rounded-full transition-all"
                style={{
                  width: i === currentIndex ? '18px' : '6px',
                  height: '6px',
                  background: i === currentIndex ? '#00D4FF' : 'rgba(255,255,255,0.4)'
                }}
              />
            ))}
            {listings?.length > 8 && (
              <span className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>+{listings?.length - 8}</span>
            )}
          </div>
          {/* Address overlay on image */}
          <div className="absolute bottom-10 left-4 right-4">
            <div className="text-sm font-semibold leading-tight" style={{ color: '#FFFFFF', textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>
              {listing?.address}
            </div>
            {listing?.price && (
              <div className="text-xs mt-0.5 font-medium" style={{ color: '#00D4FF', textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>
                {listing?.price}
              </div>
            )}
          </div>
        </div>

        {/* Card Body */}
        <div className="p-4">
          {/* Draft progress bar */}
          {listing?.status === 'draft' && (
            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1 h-1.5 rounded-full" style={{ background: 'rgba(255,184,0,0.2)' }}>
                <div className="h-full rounded-full transition-all" style={{ width: `${listing?.completion}%`, background: '#FFB800' }} />
              </div>
              <span className="text-xs font-medium" style={{ color: '#FFB800' }}>{listing?.completion}% complete</span>
            </div>
          )}

          {/* Stats row */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center gap-1.5">
              <Eye size={12} color="#4A7A9B" />
              <span className="text-xs" style={{ color: '#6B9EC4' }}>{listing?.views?.toLocaleString()} views</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span style={{ fontSize: '11px' }}>👤</span>
              <span className="text-xs" style={{ color: '#6B9EC4' }}>{listing?.leads} leads</span>
            </div>
            <span className="text-xs" style={{ color: '#4A7A9B' }}>{listing?.created}</span>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => onEdit(listing?.id)}
              className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold transition-all"
              style={{ background: 'rgba(0,212,255,0.12)', color: '#00D4FF', border: '1px solid rgba(0,212,255,0.25)' }}
            >
              <Edit2 size={12} />
              {t('edit')}
            </button>
            <button
              className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold transition-all"
              style={{ background: 'rgba(0,255,136,0.1)', color: '#00FF88', border: '1px solid rgba(0,255,136,0.25)' }}
            >
              <Eye size={12} />
              {t('view')}
            </button>
            <button
              onClick={() => setOpenMenuId(openMenuId === listing?.id ? null : listing?.id)}
              className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold transition-all"
              style={{ background: 'rgba(255,80,80,0.1)', color: '#FF5050', border: '1px solid rgba(255,80,80,0.25)' }}
            >
              <MoreVertical size={12} />
              More
            </button>
          </div>

          {/* More actions dropdown */}
          {openMenuId === listing?.id && (
            <div className="mt-2 rounded-xl overflow-hidden" style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)' }}>
              {listing?.status !== 'live' && (
                <button
                  onClick={() => { onStatusChange(listing?.id, 'live'); setOpenMenuId(null); }}
                  className="w-full text-left px-4 py-2.5 text-xs font-medium transition-colors"
                  style={{ color: '#00FF88', borderBottom: '1px solid rgba(0,212,255,0.08)' }}
                >
                  ✓ {t('publishLive')}
                </button>
              )}
              {listing?.status !== 'draft' && (
                <button
                  onClick={() => { onStatusChange(listing?.id, 'draft'); setOpenMenuId(null); }}
                  className="w-full text-left px-4 py-2.5 text-xs font-medium transition-colors"
                  style={{ color: '#FFB800', borderBottom: '1px solid rgba(0,212,255,0.08)' }}
                >
                  ✎ {t('moveToDraft')}
                </button>
              )}
              {listing?.status !== 'sold' && (
                <button
                  onClick={() => { onStatusChange(listing?.id, 'sold'); setOpenMenuId(null); }}
                  className="w-full text-left px-4 py-2.5 text-xs font-medium transition-colors"
                  style={{ color: '#00D4FF', borderBottom: '1px solid rgba(0,212,255,0.08)' }}
                >
                  $ {t('markAsSold')}
                </button>
              )}
              <button
                onClick={() => { onDelete(listing?.id); setOpenMenuId(null); }}
                className="w-full text-left px-4 py-2.5 text-xs font-medium transition-colors"
                style={{ color: '#FF5050' }}
              >
                🗑 {t('delete')}
              </button>
            </div>
          )}
        </div>
      </div>
      {/* Swipe hint */}
      <div className="flex items-center justify-center gap-1 mt-2">
        <span className="text-xs" style={{ color: '#2A4A6B' }}>{t('swipeToNavigate')}</span>
      </div>
    </div>
  );
};

const ListingsTable = ({
  listings, filter, setFilter, sortBy, sortDir, onSort,
  selectedIds, setSelectedIds, onStatusChange, onBulkStatusChange,
  onDelete, onEdit, searchQuery, setSearchQuery
}) => {
  const [openMenuId, setOpenMenuId] = useState(null);
  const { t } = useLanguage();

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev?.includes(id) ? prev?.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds?.length === listings?.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(listings?.map(l => l?.id) || []);
    }
  };

  const allSelected = listings?.length > 0 && selectedIds?.length === listings?.length;

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-5">
        <div className="flex items-center gap-1 flex-wrap">
          {FILTERS?.map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all"
              style={{
                background: filter === f ? 'rgba(0,212,255,0.15)' : 'transparent',
                color: filter === f ? '#00D4FF' : '#4A7A9B',
                border: filter === f ? '1px solid rgba(0,212,255,0.35)' : '1px solid transparent'
              }}
            >
              {f === 'all' ? t('allListings') : f?.charAt(0)?.toUpperCase() + f?.slice(1)}
            </button>
          ))}
        </div>

        <div className="relative">
          <Search size={14} color="#4A7A9B" className="absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder={t('searchAddress')}
            value={searchQuery}
            onChange={e => setSearchQuery(e?.target?.value)}
            className="pl-8 pr-4 py-2 rounded-lg text-sm outline-none w-56"
            style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)', color: '#E8F4FD' }}
          />
        </div>
      </div>
      {selectedIds?.length > 0 && (
        <div className="flex items-center gap-3 mb-4 p-3 rounded-xl" style={{
          background: 'rgba(0,212,255,0.08)',
          border: '1px solid rgba(0,212,255,0.2)'
        }}>
          <span className="text-sm font-medium" style={{ color: '#00D4FF' }}>{selectedIds?.length} {t('selected')}</span>
          <div className="flex items-center gap-2">
            {['live', 'draft', 'sold']?.map(s => (
              <button
                key={s}
                onClick={() => onBulkStatusChange(s)}
                className="px-3 py-1 rounded-lg text-xs font-semibold capitalize transition-all"
                style={{
                  background: STATUS_CONFIG?.[s]?.bg,
                  color: STATUS_CONFIG?.[s]?.color,
                  border: `1px solid ${STATUS_CONFIG?.[s]?.border}`
                }}
              >
                {t('markAs')} {s}
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Desktop Table */}
      <div className="hidden md:block rounded-2xl overflow-hidden" style={{
        border: '1px solid rgba(0,212,255,0.12)',
        background: 'rgba(0,212,255,0.02)'
      }}>
        <table className="w-full">
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(0,212,255,0.1)' }}>
              <th className="p-4 text-left w-10">
                <button onClick={toggleSelectAll}>
                  {allSelected ? <CheckSquare size={16} color="#00D4FF" /> : <Square size={16} color="#4A7A9B" />}
                </button>
              </th>
              {[['address', t('property')], ['status', t('status')], ['created', t('created')], ['views', t('views')], ['leads', t('leads')]]?.map(([col, label]) => (
                <th key={col} className="p-4 text-left cursor-pointer select-none" onClick={() => onSort(col)}>
                  <div className="flex items-center gap-1">
                    <span className="text-xs font-semibold" style={{ color: '#4A7A9B' }}>{label}</span>
                    <SortIcon col={col} sortBy={sortBy} sortDir={sortDir} />
                  </div>
                </th>
              ))}
              <th className="p-4 text-left">
                <span className="text-xs font-semibold" style={{ color: '#4A7A9B' }}>{t('actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {!listings?.length ? (
              <tr>
                <td colSpan={7} className="p-10 text-center text-sm" style={{ color: '#4A7A9B' }}>{t('noListingsFound')}</td>
              </tr>
            ) : listings?.map((listing) => {
              const sc = STATUS_CONFIG?.[listing?.status] || STATUS_CONFIG?.draft;
              return (
                <tr
                  key={listing?.id}
                  style={{ borderBottom: '1px solid rgba(0,212,255,0.06)' }}
                  className="transition-colors"
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,212,255,0.03)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <td className="p-4">
                    <button onClick={() => toggleSelect(listing?.id)}>
                      {selectedIds?.includes(listing?.id)
                        ? <CheckSquare size={16} color="#00D4FF" />
                        : <Square size={16} color="#4A7A9B" />}
                    </button>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center" style={{
                        background: 'rgba(0,212,255,0.1)',
                        border: '1px solid rgba(0,212,255,0.2)'
                      }}>
                        <span style={{ fontSize: '16px' }}>🏠</span>
                      </div>
                      <div>
                        <div className="text-sm font-medium" style={{ color: '#E8F4FD' }}>{listing?.address}</div>
                        <div className="text-xs" style={{ color: '#4A7A9B' }}>{listing?.price}</div>
                        {listing?.status === 'draft' && (
                          <div className="flex items-center gap-1 mt-1">
                            <div className="h-1 rounded-full overflow-hidden" style={{ width: '60px', background: 'rgba(255,184,0,0.2)' }}>
                              <div className="h-full rounded-full" style={{ width: `${listing?.completion}%`, background: '#FFB800' }} />
                            </div>
                            <span className="text-xs" style={{ color: '#FFB800' }}>{listing?.completion}%</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{
                      background: sc?.bg, color: sc?.color, border: `1px solid ${sc?.border}`
                    }}>
                      {sc?.label}
                    </span>
                  </td>
                  <td className="p-4"><span className="text-sm" style={{ color: '#6B9EC4' }}>{listing?.created}</span></td>
                  <td className="p-4"><span className="text-sm" style={{ color: '#6B9EC4' }}>{listing?.views?.toLocaleString()}</span></td>
                  <td className="p-4"><span className="text-sm" style={{ color: '#6B9EC4' }}>{listing?.leads}</span></td>
                  <td className="p-4">
                    <div className="flex items-center gap-1 relative">
                      <button onClick={() => onEdit(listing?.id)} className="p-1.5 rounded-lg transition-all" style={{ color: '#4A7A9B' }}
                        onMouseEnter={e => { e.currentTarget.style.color = '#00D4FF'; e.currentTarget.style.background = 'rgba(0,212,255,0.1)'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = '#4A7A9B'; e.currentTarget.style.background = 'transparent'; }}
                        title={t('edit')}
                      ><Edit2 size={14} /></button>
                      <button className="p-1.5 rounded-lg transition-all" style={{ color: '#4A7A9B' }}
                        onMouseEnter={e => { e.currentTarget.style.color = '#00D4FF'; e.currentTarget.style.background = 'rgba(0,212,255,0.1)'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = '#4A7A9B'; e.currentTarget.style.background = 'transparent'; }}
                        title={t('view')}
                      ><Eye size={14} /></button>
                      <button
                        onClick={() => setOpenMenuId(openMenuId === listing?.id ? null : listing?.id)}
                        className="p-1.5 rounded-lg transition-all" style={{ color: '#4A7A9B' }}
                        onMouseEnter={e => { e.currentTarget.style.color = '#00D4FF'; e.currentTarget.style.background = 'rgba(0,212,255,0.1)'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = '#4A7A9B'; e.currentTarget.style.background = 'transparent'; }}
                      ><MoreVertical size={14} /></button>
                      {openMenuId === listing?.id && (
                        <div className="absolute right-0 top-8 z-20 rounded-xl py-1 min-w-36" style={{ background: '#0A1628', border: '1px solid rgba(0,212,255,0.2)', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                          {listing?.status !== 'live' && (
                            <button onClick={() => { onStatusChange(listing?.id, 'live'); setOpenMenuId(null); }}
                              className="w-full text-left px-4 py-2 text-xs transition-colors" style={{ color: '#00FF88' }}
                              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,255,136,0.08)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            >{t('publishLive')}</button>
                          )}
                          {listing?.status !== 'draft' && (
                            <button onClick={() => { onStatusChange(listing?.id, 'draft'); setOpenMenuId(null); }}
                              className="w-full text-left px-4 py-2 text-xs transition-colors" style={{ color: '#FFB800' }}
                              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,184,0,0.08)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            >{t('moveToDraft')}</button>
                          )}
                          {listing?.status !== 'sold' && (
                            <button onClick={() => { onStatusChange(listing?.id, 'sold'); setOpenMenuId(null); }}
                              className="w-full text-left px-4 py-2 text-xs transition-colors" style={{ color: '#00D4FF' }}
                              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,212,255,0.08)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            >{t('markAsSold')}</button>
                          )}
                          <div style={{ borderTop: '1px solid rgba(0,212,255,0.1)', margin: '4px 0' }} />
                          <button onClick={() => { onDelete(listing?.id); setOpenMenuId(null); }}
                            className="w-full text-left px-4 py-2 text-xs transition-colors" style={{ color: '#FF5050' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,80,80,0.08)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >{t('delete')}</button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Swipeable Cards */}
      <div className="md:hidden">
        <MobileSwipeableCards
          listings={listings}
          onEdit={onEdit}
          onDelete={onDelete}
          onStatusChange={onStatusChange}
        />
      </div>
    </div>
  );
};

export default ListingsTable;
